import { z } from 'zod';
import {
  LocaleSchema,
  MoneySchema,
  ShortIdSchema,
  SlugSchema,
  TimestampSchema,
  UidSchema,
} from './primitives.js';

export const AgentStatusSchema = z.enum([
  'applied',
  'pending_kyc',
  'active',
  'suspended',
  'terminated',
]);
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

export const AgentTierSchema = z.enum(['bronze', 'silver', 'gold']);
export type AgentTier = z.infer<typeof AgentTierSchema>;

export const AgentPayoutDetailsSchema = z.object({
  bankName: z.string().optional(),
  accountName: z.string().optional(),
  accountNumber: z.string().optional(),
  duitNowId: z.string().optional().describe('NRIC or mobile number for DuitNow'),
  thresholdSen: MoneySchema.default(10000).describe('Minimum payout threshold; default RM 100'),
});

/**
 * Sprint 80 — recruiter epic foundation. Mirrors `TenantAcquisitionSchema`:
 * when an agent signs up via a recruiter's deep link, we stamp the
 * recruiter id immutably so commission splits flow to the right
 * recruiter for the agent's lifetime. `channel = 'direct'` is the
 * default for agents who signed up before recruiters existed.
 */
export const AgentAcquisitionSchema = z.object({
  channel: z.enum(['recruiter', 'direct']),
  recruiterId: ShortIdSchema.optional().describe('Set if channel = recruiter'),
  recruiterCodeUsed: z
    .string()
    .optional()
    .describe('Raw recruiter code typed/scanned at signup, for audit'),
  signedUpAt: TimestampSchema,
});
export type AgentAcquisition = z.infer<typeof AgentAcquisitionSchema>;

export const AgentProfileSchema = z.object({
  id: ShortIdSchema,
  platformBrandId: SlugSchema.describe(
    'Which platform brand this agent operates under (ADR-0017). Immutable after signup.',
  ),
  uid: UidSchema,
  name: z.string(),
  phone: z.string(),
  email: z.string().email(),
  locale: LocaleSchema.default('en'),
  status: AgentStatusSchema,
  tier: AgentTierSchema.default('bronze'),
  code: z
    .string()
    .regex(/^[A-Z]{2,4}-[A-Z0-9]{4,6}$/)
    .describe('Agent referral code'),
  payout: AgentPayoutDetailsSchema,
  kyc: z
    .object({
      submittedAt: TimestampSchema.optional(),
      verifiedAt: TimestampSchema.optional(),
      verifierUid: UidSchema.optional(),
      icScanRef: z.string().optional(),
    })
    .default({}),
  commissionOverrides: z
    .object({
      recurringRate: z.number().min(0).max(1).optional(),
      perOutletRate: z.number().min(0).max(1).optional(),
      tierMultiplier: z.number().positive().optional(),
    })
    .default({}),
  /**
   * Sprint 80 — optional acquisition record for the recruiter epic.
   * Absent for legacy agents (channel implicitly = 'direct'); set
   * immutably on signup for agents who came through a recruiter.
   */
  acquisition: AgentAcquisitionSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type AgentProfile = z.infer<typeof AgentProfileSchema>;

export const EarningsLedgerEntrySchema = z.object({
  id: ShortIdSchema,
  kind: z.enum(['accrued', 'clawed_back', 'adjustment', 'paid']),
  tenantId: z.string(),
  invoiceId: z.string().optional(),
  payoutId: z.string().optional(),
  basis: z.record(z.string(), z.unknown()),
  rate: z.number().min(0).max(2),
  multiplier: z.number().positive().default(1),
  amount: z.number().int().describe('Sen, signed (negative for clawback)'),
  accruedAt: TimestampSchema,
  notes: z.string().optional(),
});
export type EarningsLedgerEntry = z.infer<typeof EarningsLedgerEntrySchema>;

/**
 * Back-reference doc at `agents/{agentId}/operators/{tenantId}`. Written
 * by `tenantBootstrap` at signup; patched by `commissionAccrue` on first
 * paid invoice; future status-sync triggers may keep `currentStatus`
 * fresh as the tenant lifecycle changes.
 *
 * Rationale: agents can't read `tenants/{tid}` directly (security rules
 * block cross-tenant reads), so this denormalised back-ref is the agent
 * PWA's source of truth for "who did I bring on, what's their state".
 */
export const TenantStatusInBackrefSchema = z.enum([
  'trial',
  'active',
  'past_due',
  'suspended',
  'archived',
]);
export type TenantStatusInBackref = z.infer<typeof TenantStatusInBackrefSchema>;

export const AgentOperatorBackrefSchema = z.object({
  /** Doc id — equals the `tenants/{tid}` id. Stamped by the repo on read (like
   * every other `createRepo` schema), so consumers can key/display it directly. */
  id: z.string().min(1),
  /** `tenants/{tid}` id; matches the doc id of this back-ref. */
  tenantId: z.string().min(1).optional().describe('Mirrors the doc id; convenience field.'),
  signupAt: TimestampSchema,
  /** Snapshotted at signup; refreshed by status-sync triggers when implemented. */
  currentStatus: TenantStatusInBackrefSchema.optional(),
  /** Null until the first invoice is paid; stamped by `commissionAccrue`. */
  firstPaidAt: TimestampSchema.nullable().optional(),
  /** Updated by `commissionAccrue` on every paid invoice — the most recent payment. */
  lastPaidAt: TimestampSchema.optional(),
  /** Operator-side display data, snapshotted at signup so the agent PWA can render without cross-tenant reads. */
  tenantBusinessName: z.string().optional(),
  tenantContactPhone: z.string().optional(),
  tenantContactEmail: z.string().email().optional(),
  /** The plan the operator signed up on (display only — agent doesn't see live plan changes). */
  planIdAtSignup: z.string().optional(),
});
export type AgentOperatorBackref = z.infer<typeof AgentOperatorBackrefSchema>;

export const AgentPayoutSchema = z.object({
  id: ShortIdSchema,
  periodStart: TimestampSchema,
  periodEnd: TimestampSchema,
  items: z.array(ShortIdSchema),
  grossAmount: MoneySchema,
  status: z.enum(['pending', 'processing', 'paid', 'failed']),
  method: z.enum(['duitnow', 'bank_transfer']),
  destination: AgentPayoutDetailsSchema,
  initiatedAt: TimestampSchema,
  paidAt: TimestampSchema.optional(),
  txRef: z.string().optional(),
});
export type AgentPayout = z.infer<typeof AgentPayoutSchema>;
